import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import TaskAltOutlinedIcon from '@mui/icons-material/TaskAltOutlined';
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';
import { Chip, Stack, type SxProps, type Theme } from '@mui/material';
import { memo } from 'react';
import { useTranslation } from 'react-i18next';
import type { NotificationSeverity } from '@/notifications';

interface NotificationSeverityChipProps {
  severity: NotificationSeverity;
  compact?: boolean;
}

const severityStyles: Record<
  NotificationSeverity,
  { labelKey: string; icon: typeof InfoOutlinedIcon; sx: SxProps<Theme> }
> = {
  info: {
    labelKey: 'notificationCenter.severity.info',
    icon: InfoOutlinedIcon,
    sx: {
      bgcolor: 'info.light',
      color: 'info.contrastText',
    },
  },
  success: {
    labelKey: 'notificationCenter.severity.success',
    icon: TaskAltOutlinedIcon,
    sx: {
      bgcolor: 'success.light',
      color: 'success.contrastText',
    },
  },
  warning: {
    labelKey: 'notificationCenter.severity.warning',
    icon: WarningAmberOutlinedIcon,
    sx: {
      bgcolor: 'warning.light',
      color: 'warning.contrastText',
    },
  },
  critical: {
    labelKey: 'notificationCenter.severity.critical',
    icon: ErrorOutlineIcon,
    sx: {
      bgcolor: 'error.light',
      color: 'error.contrastText',
    },
  },
};

export const NotificationSeverityChip = memo(function NotificationSeverityChip({
  severity,
  compact = false,
}: NotificationSeverityChipProps) {
  const { t } = useTranslation('entity-portal');
  const config = severityStyles[severity];
  const Icon = config.icon;

  return (
    <Chip
      size={compact ? 'small' : 'medium'}
      label={
        <Stack direction="row" spacing={0.5} alignItems="center">
          <Icon sx={{ fontSize: compact ? 14 : 16 }} />
          <span>{t(config.labelKey)}</span>
        </Stack>
      }
      sx={{ ...config.sx }}
    />
  );
});
